/**
 * Admin API Slice
 *
 * RTK Query endpoints for admin dashboard functionality.
 */

import { api } from '../api';

// ============================================
// Types
// ============================================

export interface AdminOverviewDto {
  userCount: number;
  publicProfileCount: number;
  marketplaceCount: number;
}

export interface AdminUserListItem {
  id: string;
  fullName: string;
  email: string;
  role: string;
  createdAt: string;
  isHireable: boolean;
  publicProfileSlug: string | null;
}

export interface AdminUsersResponse {
  items: AdminUserListItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface AdminPublicProfileItem {
  id: string;
  userId: string;
  displayName: string;
  slug: string;
  isPublic: boolean;
  isHidden: boolean;
  isHireable: boolean;
  hourlyRate: number | null;
  createdAt: string;
}

export interface AdminPublicProfilesResponse {
  items: AdminPublicProfileItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export type ReportTargetType = 'profile' | 'session' | 'pitch' | 'expert';
export type ReportStatus = 'open' | 'resolved' | 'dismissed';

export interface ReportDto {
  id: string;
  reporterUserId: string | null;
  targetType: ReportTargetType;
  targetId: string;
  reason: string;
  createdAt: string;
  status: ReportStatus;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
}

export interface AdminReportsResponse {
  items: ReportDto[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  openCount: number;
}

export interface ReportStatsDto {
  total: number;
  open: number;
  resolved: number;
  dismissed: number;
  byType: {
    profile: number;
    session: number;
    pitch: number;
    expert: number;
  };
}

export type AnalyticsEventType =
  | 'session_started'
  | 'session_completed'
  | 'voice_turn_recorded'
  | 'demo_run'
  | 'pitch_listen_snippet'
  | 'pitch_listen_full'
  | 'upgrade_cta_clicked'
  | 'plan_checkout_started'
  | 'profile_shared'
  | 'feedback_rated';

export interface AnalyticsSummaryDto {
  totalEvents: number;
  eventsByType: Record<AnalyticsEventType, number>;
  uniqueUsers: number;
  avgFeedbackRating: number | null;
  feedbackCount: number;
  topScenarios: Array<{ scenarioKey: string; count: number }>;
}

export interface AnalyticsEventDto {
  id: string;
  userId: string | null;
  type: AnalyticsEventType;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface AnalyticsEventsResponse {
  events: AnalyticsEventDto[];
  total: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ============================================
// API Slice
// ============================================

export const adminApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ==================
    // Overview
    // ==================

    getAdminOverview: builder.query<AdminOverviewDto, void>({
      query: () => '/admin/overview',
      transformResponse: (response: ApiResponse<AdminOverviewDto>) => response.data,
    }),

    // ==================
    // Users
    // ==================

    getAdminUsers: builder.query<
      AdminUsersResponse,
      { page?: number; pageSize?: number; q?: string }
    >({
      query: ({ page = 1, pageSize = 20, q }) => ({
        url: '/admin/users',
        params: { page, pageSize, q },
      }),
      transformResponse: (response: ApiResponse<AdminUsersResponse>) => response.data,
    }),

    // ==================
    // Public Profiles
    // ==================

    getAdminPublicProfiles: builder.query<
      AdminPublicProfilesResponse,
      { page?: number; pageSize?: number; q?: string; onlyHidden?: boolean }
    >({
      query: ({ page = 1, pageSize = 20, q, onlyHidden }) => ({
        url: '/admin/public-profiles',
        params: { page, pageSize, q, onlyHidden },
      }),
      transformResponse: (response: ApiResponse<AdminPublicProfilesResponse>) => response.data,
      providesTags: ['AdminProfiles'],
    }),

    hidePublicProfile: builder.mutation<{ id: string; isHidden: boolean }, string>({
      query: (id) => ({
        url: `/admin/public-profiles/${id}/hide`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ id: string; isHidden: boolean }>) => response.data,
      invalidatesTags: ['AdminProfiles'],
    }),

    unhidePublicProfile: builder.mutation<{ id: string; isHidden: boolean }, string>({
      query: (id) => ({
        url: `/admin/public-profiles/${id}/unhide`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ id: string; isHidden: boolean }>) => response.data,
      invalidatesTags: ['AdminProfiles'],
    }),

    // ==================
    // Reports
    // ==================

    getAdminReports: builder.query<
      AdminReportsResponse,
      { page?: number; pageSize?: number; status?: ReportStatus }
    >({
      query: ({ page = 1, pageSize = 20, status }) => ({
        url: '/admin/reports',
        params: { page, pageSize, status },
      }),
      transformResponse: (response: ApiResponse<AdminReportsResponse>) => response.data,
      providesTags: ['AdminReports'],
    }),

    resolveReport: builder.mutation<ReportDto, string>({
      query: (id) => ({
        url: `/admin/reports/${id}/resolve`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<ReportDto>) => response.data,
      invalidatesTags: ['AdminReports'],
    }),

    dismissReport: builder.mutation<ReportDto, string>({
      query: (id) => ({
        url: `/admin/reports/${id}/dismiss`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<ReportDto>) => response.data,
      invalidatesTags: ['AdminReports'],
    }),

    // ==================
    // Analytics
    // ==================

    getAnalyticsSummary: builder.query<AnalyticsSummaryDto, void>({
      query: () => '/admin/analytics/summary',
      transformResponse: (response: ApiResponse<AnalyticsSummaryDto>) => response.data,
    }),

    getAnalyticsEvents: builder.query<
      AnalyticsEventsResponse,
      { limit?: number; offset?: number; type?: AnalyticsEventType }
    >({
      query: ({ limit = 100, offset = 0, type }) => ({
        url: '/admin/analytics/events',
        params: { limit, offset, type },
      }),
      transformResponse: (response: ApiResponse<AnalyticsEventsResponse>) => response.data,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminOverviewQuery,
  useGetAdminUsersQuery,
  useGetAdminPublicProfilesQuery,
  useHidePublicProfileMutation,
  useUnhidePublicProfileMutation,
  useGetAdminReportsQuery,
  useResolveReportMutation,
  useDismissReportMutation,
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsEventsQuery,
} = adminApi;
